import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";
import { SEO_BASE_URL, internalLinkTargets } from "../../data/seoPages";

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, intro, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
        {title}
      </h2>
      {intro ? (
        <p className="mt-3 max-w-[780px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
          {intro}
        </p>
      ) : null}
      <div className="mt-6 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
        {children}
      </div>
    </section>
  );
}

function SignalCard({ eyebrow, title, desc, tone = "neutral" }) {
  const toneClasses =
    tone === "accent"
      ? "border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,241,242,0.96))]"
      : tone === "cool"
        ? "border-[#DBEAFE] bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(239,246,255,0.96))]"
        : "border-[#E5E7EB] bg-white/92";

  return (
    <div className={`rounded-[30px] border p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] ${toneClasses}`}>
      <p className={`text-[12px] font-semibold tracking-[0.14em] ${tone === "cool" ? "text-[#1D4ED8]" : "text-[#EF4949]"}`}>
        {eyebrow}
      </p>
      <h3 className="mt-3 font-segoe text-[22px] font-semibold text-[#111827]">{title}</h3>
      <p className="mt-3 text-[15px] leading-7 text-[#6B7280]">{desc}</p>
    </div>
  );
}

function EditorialCard({ eyebrow, title, meta, desc, tone = "neutral" }) {
  const toneClasses =
    tone === "accent"
      ? "border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,241,242,0.96))]"
      : tone === "cool"
        ? "border-[#DBEAFE] bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(239,246,255,0.96))]"
        : "border-[#E5E7EB] bg-white/92";

  return (
    <div className={`rounded-[30px] border p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] ${toneClasses}`}>
      <p className={`text-[12px] font-semibold tracking-[0.14em] ${tone === "cool" ? "text-[#1D4ED8]" : "text-[#EF4949]"}`}>
        {eyebrow}
      </p>
      <h3 className="mt-3 font-segoe text-[22px] font-semibold text-[#111827] leading-tight">
        {title}
      </h3>
      <p className="mt-2 text-[13px] font-semibold tracking-[0.06em] text-[#6B7280] uppercase">
        {meta}
      </p>
      <p className="mt-4 text-[15px] leading-7 text-[#6B7280]">{desc}</p>
    </div>
  );
}

function StepCard({ index, text }) {
  return (
    <div className="rounded-[30px] border border-[#FEE2E2] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,245,245,0.98))] p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
      <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">Step 0{index}</p>
      <p className="mt-3 text-[16px] leading-7 text-[#4B5563]">{text}</p>
    </div>
  );
}

function TravelTipCard({ index, text }) {
  return (
    <div className="rounded-[30px] border border-[#E5E7EB] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.96))] p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
      <p className="text-[12px] font-semibold tracking-[0.14em] text-[#0F766E]">
        Travel Tip 0{index}
      </p>
      <p className="mt-3 text-[16px] leading-7 text-[#4B5563]">{text}</p>
    </div>
  );
}

function ChecklistCard({ title, items, eyebrow, tone = "neutral" }) {
  const toneClasses =
    tone === "dark"
      ? "border-[#111827] bg-[#111827] text-white shadow-[0_20px_50px_rgba(15,23,42,0.18)]"
      : tone === "cool"
        ? "border-[#DBEAFE] bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(239,246,255,0.96))]"
        : "border-[#E5E7EB] bg-white/92";

  return (
    <div className={`rounded-[30px] border p-5 ${toneClasses}`}>
      <p className={`text-[12px] font-semibold tracking-[0.14em] ${tone === "dark" ? "text-[#FCA5A5]" : "text-[#EF4949]"}`}>
        {eyebrow}
      </p>
      <h3 className={`mt-3 font-segoe text-[22px] font-semibold ${tone === "dark" ? "text-white" : "text-[#111827]"}`}>
        {title}
      </h3>
      <ul className="mt-5 space-y-3">
        {items.map((item) => (
          <li
            key={item}
            className={`flex items-start gap-3 text-[15px] leading-7 ${
              tone === "dark" ? "text-[#D1D5DB]" : "text-[#4B5563]"
            }`}
          >
            <span
              className={`mt-[7px] inline-flex h-2.5 w-2.5 rounded-full ${
                tone === "dark" ? "bg-[#FCA5A5]" : "bg-[#EF4949]"
              }`}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LocationCityPage({ cityConfig }) {
  const route = `/locations/${cityConfig.city}/airbnb-grocery-delivery`;
  const title = `${cityConfig.displayName} Airbnb Grocery Delivery | Pre-Arrival Grocery Stocking | Bellhopt`;
  const description =
    cityConfig.metaDescription ||
    `Bellhopt Concierge provides pre-arrival groceries that are delivered, stocked, and organized in ${cityConfig.displayName}. 24-hour lead time required.`;
  const canonical = `${SEO_BASE_URL}${route}/`;
  const faqItems = cityConfig.localFaq || [];
  const cityTags = cityConfig.cityTags || [];
  const bestForStays = cityConfig.bestForStays || [];
  const operatorPressurePoints = cityConfig.operatorPressurePoints || [];
  const popularAttractions = cityConfig.popularAttractions || [];
  const restaurantAreas = cityConfig.restaurantAreas || [];
  const restaurantPicks = cityConfig.restaurantPicks || [];
  const vibeGuide = cityConfig.vibeGuide || [];
  const travelTips = cityConfig.travelTips || [];

  const onPageLinks = [
    ["local-market", `Why ${cityConfig.displayName} Needs More Than Basic Delivery`],
    ["city-guide", `A Local Guide To ${cityConfig.displayName}`],
    ["restaurant-picks", `Restaurant Picks In ${cityConfig.displayName}`],
    ["vibe-guide", `Pick Your Vibe In ${cityConfig.displayName}`],
    ["travel-tips", `Travel Tips For ${cityConfig.displayName}`],
    ["best-fit", `Best Fit Stays In ${cityConfig.displayName}`],
    ["how-it-works", `How Bellhopt Works In ${cityConfig.displayName}`],
    ["property-managers", `Why Property Managers Use Bellhopt In ${cityConfig.displayName}`],
    ["concierge", `During-Stay Concierge Services In ${cityConfig.displayName}`],
    ["arrival-orders", `Popular Arrival Orders In ${cityConfig.displayName}`],
    ["lead-time", `24-Hour Lead Time In ${cityConfig.displayName}`],
    ["faq", `Local FAQ For ${cityConfig.displayName}`],
  ];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Locations",
          item: `${SEO_BASE_URL}/locations/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: cityConfig.displayName,
          item: canonical,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    },
    ...(popularAttractions.length
      ? [
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `Popular Attractions In ${cityConfig.displayName}`,
            itemListElement: popularAttractions.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.name,
              description: item.detail,
            })),
          },
        ]
      : []),
    ...(restaurantPicks.length
      ? [
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `Restaurant Picks In ${cityConfig.displayName}`,
            itemListElement: restaurantPicks.map((item, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: item.name,
              description: `${item.cuisine} in ${item.neighborhood}. ${item.detail}`,
            })),
          },
        ]
      : []),
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical={`${route}/`}
        robots={cityConfig.indexable ? "index,follow" : "noindex,follow"}
        ogTitle={`${cityConfig.displayName} Airbnb Grocery Delivery | Bellhopt Concierge`}
        ogDescription={description}
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} relative overflow-hidden p-8 mobile:p-5`}>
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-24 top-0 h-64 w-64 rounded-full bg-[#FFE4E6] blur-3xl opacity-70" />
            <div className="absolute bottom-0 left-0 h-56 w-56 rounded-full bg-[#FEE2E2] blur-3xl opacity-60" />
          </div>

          <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-end">
            <div>
              <p className="text-[13px] font-semibold tracking-[0.14em] text-[#EF4949]">
                {cityConfig.displayName} Market Guide
              </p>
              <h1 className="mt-3 font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
                {cityConfig.displayName} Airbnb Grocery Delivery And Concierge Services
              </h1>
              <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
                {cityConfig.heroIntro}
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                  Get Started With Bellhopt
                </a>
                <Link to="/for/property-managers" className={SECONDARY_CTA}>
                  For Property Managers
                </Link>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {cityTags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full border border-[#FECACA] bg-[#FFF5F5] px-4 py-2 text-[12px] font-semibold tracking-[0.06em] text-[#991B1B]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="mt-4 text-[13px] text-[#6B7280]">
                Guests shop 20,000+ products. Bellhopt Concierge delivers, stocks, and organizes
                before check-in. 24-Hour Lead Time Required.
              </p>
            </div>

            <div className="rounded-[32px] border border-white/70 bg-white/85 p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)] backdrop-blur-xl">
              <div className="rounded-[26px] border border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,245,245,0.95),rgba(255,255,255,0.92))] p-5">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">
                  Local Snapshot
                </p>
                <h2 className="mt-3 font-segoe text-[26px] font-semibold leading-tight text-[#111827]">
                  Why {cityConfig.displayName} Needs More Than Basic Delivery
                </h2>
                <p className="mt-3 text-[15px] leading-7 text-[#6B7280]">
                  {cityConfig.hubSummary}
                </p>
              </div>

              <div className="mt-5 grid gap-3">
                <div className="rounded-2xl border border-[#E5E7EB] bg-white/92 px-4 py-4">
                  <h3 className="font-segoe text-[17px] font-semibold text-[#111827]">
                    Market Reality
                  </h3>
                  <p className="mt-2 text-[14px] leading-6 text-[#6B7280]">{cityConfig.marketWhy}</p>
                </div>
                <div className="rounded-2xl border border-[#E5E7EB] bg-white/92 px-4 py-4">
                  <h3 className="font-segoe text-[17px] font-semibold text-[#111827]">
                    Bellhopt's Local Value
                  </h3>
                  <p className="mt-2 text-[14px] leading-6 text-[#6B7280]">{cityConfig.trustAngle}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={`mt-6 ${GLASS_CARD_SOFT} p-6 mobile:p-4`}>
          <h2 className="font-segoe font-semibold text-[22px] text-[#111827]">On This Page</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {onPageLinks.map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className="rounded-full border border-[#E5E7EB] bg-white px-4 py-2 text-[#111827] hover:bg-[#F9FAFB] transition-all"
              >
                {label}
              </a>
            ))}
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <section id="local-market" className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
                  Why {cityConfig.displayName} Needs More Than Basic Delivery
                </h2>
                <p className="mt-3 max-w-[780px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                  These pages are built to explain how Bellhopt fits the operational reality of each
                  market. In {cityConfig.displayName}, that means understanding how guests arrive,
                  what hosts are managing, and why a stocked-and-organized service works better than
                  a doorstep handoff.
                </p>
              </div>
              <div className="rounded-2xl border border-[#E5E7EB] bg-white/90 px-4 py-3 text-[13px] font-semibold tracking-[0.08em] text-[#374151]">
                Local SEO Coverage
              </div>
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <SignalCard
                eyebrow="Guest Demand"
                title={`Arrival Expectations In ${cityConfig.displayName}`}
                desc={cityConfig.marketWhy}
                tone="accent"
              />
              <SignalCard
                eyebrow="Operations Reality"
                title={`What Hosts Manage In ${cityConfig.displayName}`}
                desc={cityConfig.operationsNotes}
                tone="cool"
              />
              <SignalCard
                eyebrow="Trust Layer"
                title={`Why Bellhopt Fits ${cityConfig.displayName}`}
                desc={cityConfig.trustAngle}
                tone="neutral"
              />
            </div>
          </section>

          <Section
            id="city-guide"
            title={`A Local Guide To ${cityConfig.displayName}`}
            intro={
              cityConfig.travelGuideIntro ||
              `Bellhopt city pages are built to help guests and hosts make smarter local recommendations in ${cityConfig.displayName}, not just explain the service.`
            }
          >
            <div className="grid gap-6 lg:grid-cols-2">
              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <h3 className="font-segoe text-[24px] font-semibold text-[#111827]">
                    Popular Attractions
                  </h3>
                  <span className="rounded-full border border-[#FECACA] bg-[#FFF5F5] px-3 py-1 text-[12px] font-semibold tracking-[0.08em] text-[#991B1B]">
                    What Guests Ask About
                  </span>
                </div>
                <div className="mt-4 grid gap-4">
                  {popularAttractions.map((item, index) => (
                    <EditorialCard
                      key={item.name}
                      eyebrow={`Attraction 0${index + 1}`}
                      title={item.name}
                      meta={item.area}
                      desc={item.detail}
                      tone={index % 2 === 0 ? "accent" : "neutral"}
                    />
                  ))}
                </div>
              </div>

              <div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <h3 className="font-segoe text-[24px] font-semibold text-[#111827]">
                    Restaurant Areas
                  </h3>
                  <span className="rounded-full border border-[#DBEAFE] bg-[#EFF6FF] px-3 py-1 text-[12px] font-semibold tracking-[0.08em] text-[#1D4ED8]">
                    Where To Eat
                  </span>
                </div>
                <div className="mt-4 grid gap-4">
                  {restaurantAreas.map((item, index) => (
                    <EditorialCard
                      key={item.area}
                      eyebrow={`Dining Area 0${index + 1}`}
                      title={item.area}
                      meta={item.vibe}
                      desc={item.detail}
                      tone={index % 2 === 0 ? "cool" : "neutral"}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Section>

          <Section
            id="restaurant-picks"
            title={`Restaurant Picks In ${cityConfig.displayName}`}
            intro={`These restaurant picks give Bellhopt guests a stronger starting point when planning standout meals in ${cityConfig.displayName}. They are meant to cover iconic names, local favorites, and at least one more premium dinner option.`}
          >
            <div className="grid gap-4 md:grid-cols-2">
              {restaurantPicks.map((item, index) => (
                <EditorialCard
                  key={item.name}
                  eyebrow={`Restaurant 0${index + 1}`}
                  title={item.name}
                  meta={`${item.cuisine} | ${item.neighborhood}`}
                  desc={item.detail}
                  tone={index % 3 === 1 ? "cool" : index % 3 === 2 ? "neutral" : "accent"}
                />
              ))}
            </div>
          </Section>

          <section id="vibe-guide" className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:items-start">
              <div className="rounded-[30px] border border-[#111827] bg-[#111827] p-6 text-white shadow-[0_20px_50px_rgba(15,23,42,0.18)]">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#FCA5A5]">
                  City Mood Guide
                </p>
                <h2 className="mt-3 font-segoe font-semibold text-[30px] mobile:text-[24px] leading-tight">
                  Pick Your Vibe In {cityConfig.displayName}
                </h2>
                <p className="mt-4 text-[16px] leading-7 text-[#D1D5DB]">
                  The same city can feel completely different depending on where the stay is
                  anchored. These recommendations help Bellhopt guests choose the version of{" "}
                  {cityConfig.displayName} that fits the trip instead of defaulting to the loudest
                  area.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {vibeGuide.map((item, index) => (
                  <div
                    key={item.vibe}
                    className={`rounded-[30px] border p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] ${
                      index === 0
                        ? "border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(255,241,242,0.96))]"
                        : index === 1
                          ? "border-[#DBEAFE] bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(239,246,255,0.96))]"
                          : "border-[#E5E7EB] bg-white/92"
                    }`}
                  >
                    <p
                      className={`text-[12px] font-semibold tracking-[0.14em] ${
                        index === 1 ? "text-[#1D4ED8]" : "text-[#EF4949]"
                      }`}
                    >
                      Vibe 0{index + 1}
                    </p>
                    <h3 className="mt-3 font-segoe text-[22px] font-semibold leading-tight text-[#111827]">
                      {item.vibe}
                    </h3>
                    <p className="mt-2 text-[13px] font-semibold tracking-[0.06em] text-[#6B7280] uppercase">
                      {item.area}
                    </p>
                    <p className="mt-4 text-[15px] leading-7 text-[#6B7280]">{item.note}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <Section
            id="travel-tips"
            title={`Travel Tips Before You Arrive In ${cityConfig.displayName}`}
            intro={`Bellhopt pages are meant to reduce trip friction, not just explain grocery stocking. These local travel tips help guests arrive better prepared in ${cityConfig.displayName}.`}
          >
            <div className="grid gap-4 md:grid-cols-2">
              {travelTips.map((item, index) => (
                <TravelTipCard key={item} index={index + 1} text={item} />
              ))}
            </div>
          </Section>

          <Section
            id="best-fit"
            title={`Best Fit Stays In ${cityConfig.displayName}`}
            intro={`These are the kinds of trips where Bellhopt tends to create the clearest guest and operator value in ${cityConfig.displayName}.`}
          >
            <div className="grid gap-4 md:grid-cols-3">
              {bestForStays.map((item, index) => (
                <SignalCard
                  key={item}
                  eyebrow={`Use Case 0${index + 1}`}
                  title={`${cityConfig.displayName} Stay Pattern`}
                  desc={item}
                  tone={index === 1 ? "cool" : "neutral"}
                />
              ))}
            </div>
          </Section>

          <section id="how-it-works" className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="grid gap-6 lg:grid-cols-[290px_minmax(0,1fr)]">
              <div className="rounded-[30px] border border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,245,245,0.96),rgba(255,255,255,0.94))] p-6">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">
                  Local Workflow
                </p>
                <h2 className="mt-3 font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827] leading-tight">
                  How Bellhopt Concierge Works In {cityConfig.displayName}
                </h2>
                <p className="mt-4 text-[16px] text-[#6B7280] leading-7">
                  Bellhopt is designed to fit the operating reality of {cityConfig.displayName},
                  not force one national delivery pattern onto every property type.
                </p>
                <div className="mt-5 rounded-2xl border border-[#FECACA] bg-white/90 px-4 py-4">
                  <h3 className="font-segoe text-[17px] font-semibold text-[#111827]">
                    In-House Execution Matters
                  </h3>
                  <p className="mt-2 text-[14px] leading-6 text-[#6B7280]">
                    Bellhopt uses in-house, trained, vetted team members for in-home execution, so
                    hosts are not relying on anonymous last-mile labor for the final arrival
                    experience.
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                {cityConfig.howItWorksNotes.map((item, index) => (
                  <StepCard key={item} index={index + 1} text={item} />
                ))}
              </div>
            </div>
          </section>

          <section id="property-managers" className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
                  Why Property Managers Use Bellhopt In {cityConfig.displayName}
                </h2>
                <p className="mt-3 max-w-[780px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                  Property managers do not need another guest promise that creates more manual
                  coordination. They need a service that can be dispatched, repeated, and trusted
                  across the properties they manage in {cityConfig.displayName}.
                </p>
              </div>
              <div className="rounded-2xl border border-[#E5E7EB] bg-white/90 px-4 py-3 text-[13px] font-semibold tracking-[0.08em] text-[#374151]">
                Operator Use Cases
              </div>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <ChecklistCard
                eyebrow="Pressure Points"
                title={`What Operators Need In ${cityConfig.displayName}`}
                items={operatorPressurePoints}
                tone="cool"
              />
              <ChecklistCard
                eyebrow="Bellhopt Value"
                title={`Why The Service Works In ${cityConfig.displayName}`}
                items={cityConfig.serviceHighlights}
                tone="dark"
              />
            </div>
          </section>

          <Section
            id="concierge"
            title={`During-Stay Concierge Services In ${cityConfig.displayName}`}
            intro={`Bellhopt is more than grocery stocking in ${cityConfig.displayName}. In the right property and market setup, the same concierge layer can support guest needs during the stay as well.`}
          >
            <div className="grid gap-4 md:grid-cols-3">
              {cityConfig.conciergeUseCases.map((item, index) => (
                <SignalCard
                  key={item}
                  eyebrow={`Concierge 0${index + 1}`}
                  title={`${cityConfig.displayName} Concierge Use Case`}
                  desc={item}
                  tone={index === 1 ? "cool" : "neutral"}
                />
              ))}
            </div>
          </Section>

          <Section
            id="arrival-orders"
            title={`Popular Arrival Orders In ${cityConfig.displayName}`}
            intro={`These examples show the kinds of pre-arrival grocery orders that perform well in ${cityConfig.displayName}. Guests can still shop from 20,000+ products and customize the cart to fit the trip.`}
          >
            <div className="grid gap-4 md:grid-cols-3">
              {cityConfig.arrivalBundle.map((bundleItem, index) => (
                <SignalCard
                  key={bundleItem}
                  eyebrow={`Bundle 0${index + 1}`}
                  title={`${cityConfig.displayName} Arrival Basket`}
                  desc={bundleItem}
                  tone={index === 0 ? "accent" : "neutral"}
                />
              ))}
            </div>
          </Section>

          <section id="lead-time" className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div>
                <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
                  The 24-Hour Lead Time Policy In {cityConfig.displayName}
                </h2>
                <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                  Bellhopt requires a minimum 24-hour lead time for pre-arrival grocery delivery and
                  grocery stocking orders. That lead time protects product quality, routing
                  reliability, property access coordination, and the final stocked-and-organized
                  result.
                </p>
                <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                  In {cityConfig.displayName}, the lead time is also what gives Bellhopt room to
                  handle the local operating details correctly instead of forcing a rushed,
                  lower-quality handoff.
                </p>
              </div>
              <div className="rounded-[30px] border border-[#111827] bg-[#111827] p-6 text-white shadow-[0_20px_50px_rgba(15,23,42,0.18)]">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#FCA5A5]">
                  Quality Guardrail
                </p>
                <h3 className="mt-3 font-segoe text-[28px] font-semibold leading-tight">
                  Reliability Beats Last-Minute Workarounds
                </h3>
                <p className="mt-4 text-[15px] leading-7 text-[#D1D5DB]">
                  If the goal is a premium arrival, Bellhopt needs enough time to shop accurately,
                  route correctly, access the property, and leave the home looking ready to use.
                </p>
              </div>
            </div>
          </section>

          <Section id="faq" title={`Local FAQ For ${cityConfig.displayName}`}>
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details
                  key={item.q}
                  className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]"
                >
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">
                    {item.q}
                  </summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Related Bellhopt Pages
            </h2>
            <p className="mt-4 max-w-[780px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Explore the broader Bellhopt knowledge base that supports this {cityConfig.displayName}{" "}
              market page, including pre-arrival groceries, vacation rental grocery delivery,
              property manager use cases, and integrations.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/locations" className={SECONDARY_CTA}>
                All Locations
              </Link>
              {internalLinkTargets.map((item) => (
                <Link key={item.to} to={item.to} className={SECONDARY_CTA}>
                  {item.label}
                </Link>
              ))}
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <div className="rounded-[32px] border border-[#FECACA] bg-[linear-gradient(135deg,rgba(255,245,245,0.96),rgba(255,255,255,0.94),rgba(254,226,226,0.72))] p-6 mobile:p-5">
              <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">
                Market CTA
              </p>
              <h2 className="mt-3 font-segoe font-semibold text-[34px] mobile:text-[26px] text-[#111827] leading-tight">
                Ready To Raise The Arrival Standard In {cityConfig.displayName}?
              </h2>
              <p className="mt-4 max-w-[760px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                Give guests a stocked, organized home before they walk in and give your team a more
                reliable concierge workflow behind the scenes.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                  Get Started
                </a>
                <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>
                  Talk To Sales
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default LocationCityPage;
